import { isReflectCron, type CronJob } from '~/hooks/queries/use-crons';
import { formatTime } from '~/lib/local-datetime';

/**
 * FRONT-2439: the system reflection schedule (`reflect-1..N`, one seeded job
 * per daily reflection time) is ONE thing to the user — the same reflection at
 * several times of day, which nobody can edit, pause or delete. The Schedule
 * list shows it as a single row; everything else stays one row per job.
 */
export type ScheduleListItem =
  | { kind: 'job'; cron: CronJob }
  | { kind: 'reflection'; crons: CronJob[]; times: string[] };

/** The one reflection row sits where the first reflect job would have (the
 *  backend pins them last, in time order); every other job keeps its place. */
export function groupScheduleList(crons: CronJob[]): ScheduleListItem[] {
  const reflect = crons.filter((c) => isReflectCron(c));
  const items: ScheduleListItem[] = [];
  let placed = false;
  for (const cron of crons) {
    if (!isReflectCron(cron)) {
      items.push({ kind: 'job', cron });
    } else if (!placed) {
      items.push({ kind: 'reflection', crons: reflect, times: reflectionTimes(reflect) });
      placed = true;
    }
  }
  return items;
}

/**
 * Each reflect job's time of day in the READER'S clock, earliest first,
 * without repeats. The seeded jobs store `timeOfDay` as a wall-clock time in
 * their own `timezone` (Europe/Berlin since FRONT-2500; older rows had none and
 * meant UTC), so the stored string is not the reader's time: a job's next
 * firing is an absolute instant and is formatted in the device zone; a job
 * with no next firing and no zone converts its UTC time of day the same way. A
 * time stored in some other zone is shown unconverted rather than guessed at.
 */
type TimedJob = { schedule?: CronJob['schedule']; nextRunAt?: string | null };

export function reflectionTimes(
  crons: TimedJob[],
  now: Date = new Date(),
  clock: (instant: string | number) => string = (i) => formatTime(i, CLOCK),
): string[] {
  const times = crons.map((c) => localReflectionTime(c, now, clock)).filter((t): t is string => Boolean(t));
  return [...new Set(times)].sort();
}

const CLOCK: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

function localReflectionTime(c: TimedJob, now: Date, clock: (instant: string | number) => string): string | null {
  if (c.nextRunAt) return clock(c.nextRunAt) || null;
  const t = c.schedule?.timeOfDay;
  if (!t) return null;
  const tz = c.schedule?.timezone;
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return t;
  const storedInOtherZone = Boolean(tz && tz !== 'UTC');
  if (storedInOtherZone) return t;
  const at = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), Number(m[1]), Number(m[2]));
  return clock(at) || t;
}

export const REFLECTION_NAME = 'Reflection';

/** "Reflection · 5× daily" — the count is however many reflect jobs exist. */
export function reflectionRowLabel(count: number): string {
  return `${REFLECTION_NAME} · ${count}× daily`;
}

export const REFLECTION_PATH = '/crons/reflection';
